// @ts-strict
import type {
  Accidental,
  LetterName,
  NoteName,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import Note from '../uTheory.Notation/note';
import Interval from '../uTheory.Notation/interval';
import type { RejectDuplicateOptions } from './rejectDuplicates';
import rejectDuplicates from './rejectDuplicates';

export function randomWhiteNotes(
  numberOfQuestions: number,
  lowestNote: Note | NoteName,
  highestNote: Note | NoteName,
  acceptableWhiteNotes: LetterName[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
  options?: RejectDuplicateOptions
) {
  return rejectDuplicates(
    getWhiteNotesInRange(lowestNote, highestNote, acceptableWhiteNotes),
    numberOfQuestions,
    options
  );
}

function getWhiteNotesInRange(
  lowestNote: Note | NoteName,
  highestNote: Note | NoteName,
  acceptableWhiteNotes: LetterName[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B']
): Note[] {
  lowestNote = new Note(lowestNote);
  highestNote = new Note(highestNote);

  // In case the lowestNote/highestNote accidentals constrict the white note range,
  // normalize them.
  if (lowestNote.accidental === '#' || lowestNote.accidental === 'x') {
    // Get a simple spelling:
    lowestNote = Note.fromMidi(lowestNote.getMidiNumber());
    // If its a black note, go up a m2
    if (lowestNote.isBlackNote) {
      lowestNote = lowestNote.getNoteAtGeneric(2);
      lowestNote.accidental = '';
    }
  }
  if (highestNote.accidental === 'b' || highestNote.accidental === 'bb') {
    // Get a simple spelling:
    highestNote = Note.fromMidi(highestNote.getMidiNumber());
    // If its a black note, go down to the next white note.
    if (highestNote.isBlackNote) {
      highestNote = highestNote.getNoteAtGeneric(-2);
      highestNote.accidental = '';
    }
  }

  const whiteNotes: Note[] = [];

  const range = Interval.getGenericInterval(lowestNote, highestNote);
  for (let i = 1; i <= range; i++) {
    const note = Interval.getNoteAtGenericInterval(lowestNote, i);
    note.accidental = '';
    if (!note) throw Error(`Unable to get generic interval ${i} from ${lowestNote}`);
    if (acceptableWhiteNotes.indexOf(note.letter) !== -1) whiteNotes.push(note);
  }
  return whiteNotes;
}

export function getAllNotesInRange(
  lowestNote: Note | NoteName,
  highestNote: Note | NoteName,
  acceptableAccidentals: Accidental[]
): Note[] {
  // We set the accidentals to naturals at first to generate white notes
  // this assures we include all possible white note letters. We'll refine
  // our range checking in the next step.
  const lowestWhiteNote = new Note(lowestNote).setAccidental('');
  const highestWhiteNote = new Note(highestNote).setAccidental('');
  const whiteNotes = getWhiteNotesInRange(lowestWhiteNote, highestWhiteNote, undefined);

  // Now we map every acceptable accidental onto those notes, and verify that all of
  // these are in fact within range
  return whiteNotes
    .map((whiteNote) => acceptableAccidentals.map((acc) => whiteNote.clone().setAccidental(acc)))
    .flat()
    .filter(
      (note) => note.isGreaterThanOrEquals(lowestNote) && note.isLessThanOrEquals(highestNote)
    );
}

export function randomNotes(
  numberOfQuestions: number,
  lowestNote: Note | NoteName,
  highestNote: Note | NoteName,
  acceptableAccidentals: Accidental[],
  options?: RejectDuplicateOptions
) {
  const allNotes = getAllNotesInRange(lowestNote, highestNote, acceptableAccidentals);
  return rejectDuplicates(allNotes, numberOfQuestions, options);
}
